import { useEffect, useState } from "react";
import clsx from "clsx";
import {
  Award,
  Gauge,
  LayoutGrid,
  Menu,
  Settings,
  StickyNote,
  Ticket,
  Wallet,
  ScrollText
} from "lucide-react";
import MenuItem from "@/components/atoms/MenuItem";
import { MenuItemType } from "@/components/atoms/MenuItem/MenuItem";
import { useMenuStore, useThemeStore, useSidebarStore } from "@/store";
import Button from "@/components/atoms/Button";
import "./index.css";
import useAccess from "@/hooks/useAccess";
import { Switch } from "@/components/atoms/Switch";

const MenuList: MenuItemType[] = [
  {
    key: "dashboard",
    title: "Dashboard",
    url: "/",
    icon: <Gauge size={20} />,
  },
  {
    key: "tickets",
    title: "Tickets",
    url: "/tickets",
    icon: <Ticket size={20} />,
  },
  {
    key: "metis",
    title: "Metis",
    url: "/metis_tickets",
    icon: <LayoutGrid size={20} />,
    permissions: ["show_metis_navigation"],
  },
  {
    key: "logs",
    title: "Logs",
    icon: <StickyNote size={20} />,
    url: "/logs",
    permissions: ["listing_logs"],
  },
  {
    key: "quality",
    title: "Quality",
    icon: <Award size={20} />,
    children: [
      {
        key: "ftr",
        title: "FTR",
        url: "/ftrs/index",
      },
      {
        key: "reviews",
        title: "Reviews",
        url: "/quality",
      },
      {
        key: "ratings",
        title: "Ratings",
        url: "/ratings",
      },
    ],
    permissions: ["show_quality_navigation"],
  },
  {
    key: "financial",
    title: "Financial",
    icon: <Wallet size={20} />,
    children: [
      {
        key: "sows",
        title: "Sows",
        url: "/sows",
        permissions: ["listing_sows"],
      },
      {
        key: "invoices",
        title: "Invoices",
        url: "/auto_invoices",
        permissions: ["listing_auto_invoices"],
      }
    ],
    permissions: ["listing_sows", "listing_auto_invoices"],
  },
  {
    key: "settings",
    title: "Settings",
    icon: <Settings size={20} />,
    children: [
      {
        key: "accounts",
        title: "Accounts",
        url: "/accounts",
        permissions: ["listing_accounts"],
      },
      {
        key: "users",
        title: "Users",
        url: "/admin/users",
        permissions: ["listing_users"],
      },
      {
        key: "notices",
        title: "Notices",
        url: "/notices",
        permissions: ["listing_notices"],
      },
      {
        key: "roles",
        title: "Roles & Permissions",
        url: "/roles",
        permissions: ["listing_roles"],
      },
      {
        key: "globals",
        title: "Global Settings",
        url: "/admin/globals",
        permissions: ["listing_globals"],
      }
    ],
    permissions: ["listing_accounts", "listing_users", "listing_globals", "listing_notices"],
  },
];

const Sidebar = () => {
  const { getAccess } = useAccess();
  const active = useMenuStore((state) => state.active);
  const isOpen = useSidebarStore.use.isOpenSidebar();
  const setActive = useMenuStore((state) => state.setActive);
  const setSideBarState = useSidebarStore.use.setSideBarState();
  const theme = useThemeStore((state) => state.theme);
  const setTheme = useThemeStore((state) => state.setTheme);
  const sidebarClasses = clsx(
    "sidebar top-0 shadow-xl h-screen overflow-x-hidden fixed bg-background p-5 w-64 z-50",
    {
      opened: isOpen,
      closed: !isOpen,
    }
  );

  useEffect(() => {
    const path = window.location.pathname;
    MenuList.find((item) => {
      if (item.url && path.includes(item.url)) {
        setActive(item.key);
      } else if (item.children?.length) {
        item.children.find(
          (child) =>
            child.url && path.includes(child.url) && setActive(child.key)
        );
      }
    });
  }, []);

  const sidebarBodyClasses = clsx("flex items-center", {
    "justify-between": isOpen,
    "justify-end": !isOpen,
  });

  useEffect(() => {
    if (window.innerWidth < 1024) {
      setSideBarState(false);
    }

    window.addEventListener("resize", () => {
      setSideBarState(window.innerWidth > 1024);
    });
  }, []);

  return (
    <>
      <div className={sidebarClasses}>
        <div className={sidebarBodyClasses}>
          {isOpen ? (
            <img
              src={
                theme === "light" ? "/images/logo_mark.png" : "/images/dark-logo.png"
              }
              alt="logo"
              className="w-24"
            />
          ) : null}
          <Button
            variant="link"
            className="p-0"
            onClick={() => setSideBarState(!isOpen)}
          >
            <Menu size={20} />
          </Button>
        </div>
        <ul className="mt-6">
          {MenuList.filter(item => 
            !item.permissions || item.permissions.some(permission => getAccess([permission]))
          ).map((item) => {
            const visibleChildren = item.children?.filter(child =>
              !child.permissions || child.permissions.some(permission => getAccess([permission]))
            );

            return (
              <MenuItem
                item={{ ...item, children: visibleChildren }}
                isOpen={isOpen}
                key={item.key}
                activeItem={active}
                setOpen={setSideBarState}
                onClick={(i: MenuItemType, type) => {
                  return type !== "parent" && setActive(i.key);
                }}
              />
            );
          })}
        </ul>
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-2">
          <span className="text-sm font-medium">Light</span>
          <Switch
            checked={theme === "dark"}
            onCheckedChange={(checked) => setTheme(checked ? "dark" : "light")}
          />
          <span className="text-sm font-medium">Dark</span>
        </div>

      </div>
      <div
        className={clsx("sidebar h-screen hidden lg:block", {
          "min-w-16": !isOpen,
          "min-w-64": isOpen,
        })}
      />
      <div
        className={clsx(
          "lg:hidden fixed top-0 left-0 h-full w-full bg-black/50 z-40",
          {
            hidden: !isOpen,
          }
        )}
        onClick={() => setSideBarState(false)}
      />
    </>
  );
};

export default Sidebar;
